import { useState } from "react";
import { extractErrorMessage } from "@/core/api/errors";
import { EmailForwardingInstructions } from "./email-forwarding-instructions";
import { ForwardingOverview } from "./forwarding-overview";
import { ForwardingTestCard } from "./forwarding-test-card";
import { OtherSourcesSection } from "./other-sources-section";
import {
  useSaveOnboardingAnswers,
  useStartForwardingCheck,
} from "./shared-query";
import { StepLayout } from "./step-layout";
import { StepNavigation } from "./step-navigation";
import {
  EMAIL_INVOICE_SOURCES,
  type ForwardingCheck,
  type OnboardingAnswers,
} from "./types";

// Keep the same single-address rule as BusinessOnboarding::EMAIL_ANSWER.
const EMAIL_ANSWER = /^[^@\s,;:<>"]+@[^@\s,;:<>"]+\.[a-z]{2,}$/i;

export const ForwardingSetupScreen = ({
  businessId,
  address,
  savedAnswers,
  check,
}: {
  businessId: string;
  address: string | null;
  savedAnswers: OnboardingAnswers;
  check: ForwardingCheck;
}) => {
  const [inbox, setInbox] = useState(
    savedAnswers.forwarding_inbox_address ?? "",
  );
  const [sendError, setSendError] = useState<string>();
  const saveAnswers = useSaveOnboardingAnswers(businessId);
  const startCheck = useStartForwardingCheck(businessId);
  const trimmed = inbox.trim();
  const validInbox = EMAIL_ANSWER.test(trimmed);
  const inboxError =
    trimmed && !validInbox
      ? "Enter one email address, like ap@yourcompany.com."
      : undefined;
  const sources = savedAnswers.invoice_sources ?? [];
  const byEmail = sources.some((source) =>
    EMAIL_INVOICE_SOURCES.includes(source),
  );
  const otherSources = sources.filter(
    (source) => !EMAIL_INVOICE_SOURCES.includes(source),
  );

  // The probe goes to the saved address, so an address the customer has typed but not
  // saved has to be saved first, or they would be testing the previous one.
  const send = async () => {
    if (!validInbox) return;
    setSendError(undefined);
    try {
      if (trimmed !== savedAnswers.forwarding_inbox_address) {
        await saveAnswers.mutateAsync({ forwarding_inbox_address: trimmed });
      }
      await startCheck.mutateAsync();
    } catch (failure) {
      setSendError(extractErrorMessage(failure).message);
    }
  };

  return (
    <StepLayout
      activePane="forwarding-setup"
      title="Send your invoices to Volca"
      subtitle={
        byEmail
          ? "Set up automatic email forwarding to send new vendor invoices from your inbox to Volca for processing. You will still receive these emails. Follow the steps below to complete setup."
          : "Your invoices do not arrive by email, so you do not need a forwarding rule. You can upload invoice files in the next step."
      }
      nav={
        <StepNavigation
          businessId={businessId}
          step="forwarding_setup"
          answers={{ forwarding_inbox_address: trimmed }}
          nextDisabled={byEmail && !validInbox}
        />
      }
    >
      {/* Set the forwarding up first, then give us the mailbox and test it. */}
      {byEmail ? (
        <EmailForwardingInstructions address={address} check={check} />
      ) : (
        <ForwardingOverview address={address} byEmail={false} />
      )}

      {byEmail && (
        <ForwardingTestCard
          check={check}
          address={address}
          inbox={inbox}
          onInboxChange={setInbox}
          canSend={Boolean(validInbox && address)}
          sending={saveAnswers.isPending || startCheck.isPending}
          error={sendError}
          inboxError={inboxError}
          onSend={() => void send()}
        />
      )}

      {/* Only alongside a forwarding rule. A customer with no email invoices at all
          has nothing to route from, and the next step takes the files directly, so a
          card of scanning advice is work we are inventing for them. */}
      {byEmail && <OtherSourcesSection sources={otherSources} />}
    </StepLayout>
  );
};
